import {
  Article,
  Chat,
  Close,
  Code,
  EmojiObjects,
  ExpandLess,
  ExpandMore,
  Psychology,
  Search,
} from "@mui/icons-material";
import {
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { Memory, Message } from "../../types";
import { truncate } from "../../utils";

export interface SimpleDialogProps {
  open: boolean;
  message: Message | null | undefined;
  onClose: () => void;
}

// Get icon for memory type
const getMemoryIcon = (memory: Memory) => {
  // Check memoryType first (function-call, etc.)
  const memoryType = memory.memoryType?.toLowerCase() || "";
  const type = memory.type?.toLowerCase() || "";

  // Handle memory-related types (long-term memories, conversation memories, etc.)
  if (memoryType.includes("memories") || type.includes("memory")) {
    return <EmojiObjects />;
  }

  // Handle message memories
  if (
    memoryType.includes("message") ||
    type.includes("message") ||
    type.includes("chat")
  ) {
    return <Chat />;
  }

  // Handle search memories
  if (memoryType.includes("search") || type.includes("search")) {
    return <Search />;
  }

  // For function-call memory types, use the type field (which contains the tool name)
  // For example: type="search_news" or type="read_website_provide_summary"
  if (memoryType.includes("function")) {
    if (type.includes("search") || type.includes("news")) {
      return <Search />;
    } else if (type.includes("code")) {
      return <Code />;
    } else if (
      type.includes("document") ||
      type.includes("file") ||
      type.includes("website")
    ) {
      return <Article />;
    }
    return <Psychology />; // Generic tool icon
  }

  // Fallback to type field for non-function-call memories
  if (type.includes("document") || type.includes("file")) {
    return <Article />;
  } else if (type.includes("code")) {
    return <Code />;
  } else if (type.includes("search") || type.includes("web")) {
    return <Search />;
  }

  return <Psychology />;
};

// Individual memory card component
const MemoryCard = ({ memory }: { memory: Memory }) => {
  const [expanded, setExpanded] = useState(false);

  const normalizeContent = (c: any) => {
    if (typeof c === "string") {
      return c;
    }
    if (typeof c === "object") {
      return JSON.stringify(c, null, 2);
    }
    return "";
  };

  const score = (memory.score || 0).toFixed(2);
  const content = normalizeContent(memory.content);

  // Display name is the type field (which contains the tool name for function calls)
  const displayName = memory.type;

  return (
    <Card
      variant="outlined"
      sx={{
        mb: 2,
        bgcolor: "transparent",
        cursor: "pointer",
        transition: "all 0.2s",
        "&:hover": {
          bgcolor: "action.hover",
        },
      }}
    >
      <Box
        sx={{ display: "flex", alignItems: "center", gap: 2, p: 2 }}
        onClick={() => setExpanded(!expanded)}
      >
        <Avatar
          sx={{
            width: 32,
            height: 32,
            bgcolor: "primary.main",
          }}
        >
          {getMemoryIcon(memory)}
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <Typography variant="body2" fontWeight="medium">
              {displayName}
            </Typography>
            <Chip
              label={`Score: ${score}`}
              size="small"
              color="primary"
              sx={{
                height: 20,
                fontSize: "0.7rem",
              }}
            />
          </Box>
          <Typography variant="caption" color="text.secondary">
            {truncate(content, 100)}
          </Typography>
        </Box>
        <IconButton size="small">
          {expanded ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </Box>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Divider />
        <Box sx={{ p: 2 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            fontWeight="medium"
            sx={{ mb: 1, display: "block" }}
          >
            Content:
          </Typography>
          <Box
            component="pre"
            sx={{
              p: 2,
              bgcolor: "background.default",
              borderRadius: 1,
              fontSize: "0.875rem",
              overflow: "auto",
              maxHeight: "400px",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {content}
          </Box>
        </Box>
      </Collapse>
    </Card>
  );
};

export default (props: SimpleDialogProps) => {
  const { onClose, message, open } = props;

  if (!message) {
    return <div></div>;
  }

  return (
    <Dialog
      onClose={() => onClose()}
      open={open}
      scroll="paper"
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        Message Context
        <IconButton
          aria-label="close"
          onClick={() => onClose()}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {message.context.memories.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No context available for this message.
          </Typography>
        ) : (
          <Box sx={{ mt: 1 }}>
            {message.context.memories.map((m) => (
              <MemoryCard key={m.id} memory={m} />
            ))}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};
